import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getAllProjectsAdmin, deleteProjectAdmin, adminLogout } from '../../services/adminApi';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, LogOut, Image as ImageIcon } from 'lucide-react';

const AdminDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await getAllProjectsAdmin();
      setProjects(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load projects. Please login again.',
        variant: 'destructive'
      });
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (projectId, projectTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${projectTitle}"?`)) {
      return;
    }

    try {
      await deleteProjectAdmin(projectId);
      toast({
        title: 'Project Deleted',
        description: `"${projectTitle}" has been deleted successfully.`
      });
      loadProjects();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete project.',
        variant: 'destructive'
      });
    }
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
    toast({
      title: 'Logged Out',
      description: 'You have been logged out successfully.'
    });
  };

  const filteredProjects = filter === 'all' 
    ? projects 
    : projects.filter(p => p.category === filter);

  const residential = projects.filter(p => p.category === 'residential');
  const commercial = projects.filter(p => p.category === 'commercial');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0f0f10] flex items-center justify-center">
        <div className="text-[#4b63eb] text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f0f10] pb-12">
      {/* Header */}
      <div className="bg-[#1a1a1a] border-b border-[#2d3748]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-[#f5f5f5]">Admin Dashboard</h1>
              <p className="text-[#a0a0a0] mt-1">Manage your portfolio projects</p>
            </div>
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="text-[#a0a0a0] hover:text-[#f5f5f5] text-sm"
              >
                View Website
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-[#a0a0a0] hover:text-[#f04848] transition-colors"
              >
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#2d3748]">
            <div className="text-[#a0a0a0] text-sm mb-1">Total Projects</div>
            <div className="text-3xl font-bold text-[#f5f5f5]">{projects.length}</div>
          </div>
          <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#2d3748]">
            <div className="text-[#a0a0a0] text-sm mb-1">Residential</div>
            <div className="text-3xl font-bold text-[#4b63eb]">{residential.length}</div>
          </div>
          <div className="bg-[#1a1a1a] p-6 rounded-lg border border-[#2d3748]">
            <div className="text-[#a0a0a0] text-sm mb-1">Commercial</div>
            <div className="text-3xl font-bold text-[#4b63eb]">{commercial.length}</div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex space-x-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                filter === 'all'
                  ? 'bg-[#4b63eb] text-white'
                  : 'bg-[#1a1a1a] text-[#a0a0a0] hover:bg-[#2d3748]'
              }`}
            >
              All ({projects.length})
            </button>
            <button
              onClick={() => setFilter('residential')}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                filter === 'residential'
                  ? 'bg-[#4b63eb] text-white'
                  : 'bg-[#1a1a1a] text-[#a0a0a0] hover:bg-[#2d3748]'
              }`}
            >
              Residential ({residential.length})
            </button>
            <button
              onClick={() => setFilter('commercial')}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors ${
                filter === 'commercial'
                  ? 'bg-[#4b63eb] text-white'
                  : 'bg-[#1a1a1a] text-[#a0a0a0] hover:bg-[#2d3748]'
              }`}
            >
              Commercial ({commercial.length})
            </button>
          </div>
          <Link
            to="/admin/projects/new"
            className="flex items-center space-x-2 bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-white px-6 py-2 rounded font-semibold hover:shadow-lg hover:shadow-[#4b63eb]/50 transition-all"
          >
            <Plus size={20} />
            <span>Add Project</span>
          </Link>
        </div>

        {/* Projects List */}
        <div className="space-y-4">
          {filteredProjects.length === 0 ? (
            <div className="bg-[#1a1a1a] p-12 rounded-lg border border-[#2d3748] text-center">
              <ImageIcon size={48} className="text-[#2d3748] mx-auto mb-4" />
              <p className="text-[#a0a0a0]">No projects found</p>
            </div>
          ) : (
            filteredProjects.map((project) => (
              <div
                key={project.id}
                className="bg-[#1a1a1a] p-6 rounded-lg border border-[#2d3748] hover:border-[#4b63eb] transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-xl font-semibold text-[#f5f5f5]">{project.title}</h3>
                      <span className={`px-3 py-1 rounded text-xs font-semibold ${
                        project.category === 'residential'
                          ? 'bg-[#4b63eb]/20 text-[#4b63eb]'
                          : 'bg-[#3b4ec9]/20 text-[#3b4ec9]'
                      }`}>
                        {project.category.toUpperCase()}
                      </span>
                    </div>
                    <p className="text-[#a0a0a0] text-sm mb-3">{project.description}</p>
                    <div className="flex items-center space-x-4 text-sm text-[#a0a0a0]">
                      <span>📍 {project.location}</span>
                      <span>📅 {project.year}</span>
                      <span>🖼️ {project.images.length} images</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <Link
                      to={`/admin/projects/edit/${project.id}`}
                      className="p-2 bg-[#2d3748] hover:bg-[#4b63eb] text-[#f5f5f5] rounded transition-colors"
                      title="Edit project"
                    >
                      <Edit size={18} />
                    </Link>
                    <button
                      onClick={() => handleDelete(project.id, project.title)}
                      className="p-2 bg-[#2d3748] hover:bg-[#f04848] text-[#f5f5f5] rounded transition-colors"
                      title="Delete project"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
